import React, { useState, useMemo } from 'react';
import {
  Layout,
  Typography,
  Button,
  Table,
  Tag,
  Space,
  Input,
  Select,
  Avatar,
  Dropdown,
  Badge,
  Tabs,
  Card,
  Statistic,
  Row,
  Col,
  Tooltip,
  Modal,
  Form,
  message,
  Popconfirm,
  theme,
} from 'antd';
import {
  UserOutlined,
  PlusOutlined,
  SearchOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  ExportOutlined,
  MoreOutlined,
  CrownOutlined,
  ToolOutlined,
  TeamOutlined,
  CopyOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  MailOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { getChartSeriesColors, hexToRgba } from '../../shared/styles';

const { Title, Text } = Typography;
const { Content } = Layout;

const UserManagementAntD = () => {
  const [activeRole, setActiveRole] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [filterStatus, setFilterStatus] = useState('all');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [form] = Form.useForm();
  const { token } = theme.useToken();
  const chartColors = useMemo(() => getChartSeriesColors(token), [token]);

  // Mock data
  const users = [
    { id: 35, firstName: 'John', lastName: 'Wayne', email: 'tuner2@mail.com', role: 'tuner', status: 'active', lastActive: '2 min', requests: 145, joinedAt: '2024-01-15' },
    { id: 28, firstName: 'Arbi', lastName: 'Ben Ali', email: 'arbi.benali@aims-consult.com', role: 'client', status: 'active', lastActive: '5 min', requests: 89, credits: 140, joinedAt: '2024-02-20' },
    { id: 27, firstName: 'Nael', lastName: 'Legrand', email: 'lrtmoto@gmail.com', role: 'client', status: 'active', lastActive: '1h', requests: 234, credits: 85, joinedAt: '2023-11-08' },
    { id: 26, firstName: 'Léo', lastName: 'Corvitto', email: 'horsepower.ge@gmail.com', role: 'client', status: 'active', lastActive: '3h', requests: 156, credits: 220, joinedAt: '2023-12-01' },
    { id: 25, firstName: 'Aurelien', lastName: 'Turban', email: 'aurelien.turban@gmail.com', role: 'client', status: 'active', lastActive: '1 jour', requests: 45, credits: 60, joinedAt: '2024-03-10' },
    { id: 24, firstName: 'Mohammed', lastName: 'El Idrissi', email: 'mohamorocco38@gmail.com', role: 'client', status: 'inactive', lastActive: '5 jours', requests: 12, credits: 0, joinedAt: '2024-01-25' },
    { id: 23, firstName: 'CARXTREM', lastName: 'FHL GROUPE', email: 'carxtrem03@gmail.com', role: 'client', status: 'active', lastActive: '30 min', requests: 312, credits: 450, joinedAt: '2023-06-15' },
    { id: 22, firstName: 'Florian', lastName: 'Crusem', email: 'dmr.dev57@gmail.com', role: 'client', status: 'active', lastActive: '2h', requests: 78, credits: 95, joinedAt: '2024-02-01' },
    { id: 21, firstName: 'Julien', lastName: 'SEBAG', email: 'julsebag@gmail.com', role: 'client', status: 'pending', lastActive: 'Jamais', requests: 0, credits: 50, joinedAt: '2024-03-20' },
    { id: 20, firstName: 'Julien', lastName: 'Sebag', email: 'julien@gabesholding.com', role: 'admin', status: 'active', lastActive: '1 min', requests: null, joinedAt: '2023-01-01' },
    { id: 19, firstName: 'Thomas', lastName: 'Martin', email: 'thomas.m@angi.com', role: 'tuner', status: 'active', lastActive: '5 min', requests: 289, joinedAt: '2023-03-15' },
    { id: 18, firstName: 'Julie', lastName: 'Rousseau', email: 'julie.r@angi.com', role: 'tuner', status: 'active', lastActive: '15 min', requests: 198, joinedAt: '2023-05-20' },
    { id: 17, firstName: 'Marc', lastName: 'Dubois', email: 'marc.d@angi.com', role: 'tuner', status: 'inactive', lastActive: '3 jours', requests: 156, joinedAt: '2023-07-10' },
    { id: 16, firstName: 'Sophie', lastName: 'Laurent', email: 'sophie.l@angi.com', role: 'tuner', status: 'active', lastActive: '1h', requests: 134, joinedAt: '2023-09-01' },
    { id: 15, firstName: 'Nicolas', lastName: 'Admin', email: 'nicolas@angi.com', role: 'admin', status: 'active', lastActive: 'Maintenant', requests: null, joinedAt: '2022-01-01' },
  ];

  const roleConfig = {
    admin: { label: 'Administrateur', color: 'red', icon: <CrownOutlined /> },
    tuner: { label: 'Tuner', color: 'purple', icon: <ToolOutlined /> },
    client: { label: 'Client', color: 'green', icon: <UserOutlined /> },
  };

  const statusConfig = {
    active: { label: 'Actif', color: 'success', icon: <CheckCircleOutlined /> },
    inactive: { label: 'Inactif', color: 'default', icon: <CloseCircleOutlined /> },
    pending: { label: 'En attente', color: 'warning', icon: <ClockCircleOutlined /> },
  };

  // Filtered data
  const filteredUsers = useMemo(() => {
    let result = [...users];

    if (activeRole !== 'all') {
      result = result.filter(u => u.role === activeRole);
    }

    if (filterStatus !== 'all') {
      result = result.filter(u => u.status === filterStatus);
    }

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(u =>
        u.firstName.toLowerCase().includes(query) ||
        u.lastName.toLowerCase().includes(query) ||
        u.email.toLowerCase().includes(query)
      );
    }

    return result;
  }, [users, activeRole, filterStatus, searchQuery]);

  // Role counts
  const roleCounts = useMemo(() => ({
    all: users.length,
    admin: users.filter(u => u.role === 'admin').length,
    tuner: users.filter(u => u.role === 'tuner').length,
    client: users.filter(u => u.role === 'client').length,
  }), [users]);

  // Stats for selected role
  const roleStats = useMemo(() => {
    const roleUsers = activeRole === 'all' ? users : users.filter(u => u.role === activeRole);
    return {
      total: roleUsers.length,
      active: roleUsers.filter(u => u.status === 'active').length,
      inactive: roleUsers.filter(u => u.status === 'inactive').length,
      totalCredits: roleUsers.reduce((sum, u) => sum + (u.credits || 0), 0),
      totalRequests: roleUsers.reduce((sum, u) => sum + (u.requests || 0), 0),
    };
  }, [users, activeRole]);

  // Copy email to clipboard
  const copyEmail = (email) => {
    navigator.clipboard.writeText(email);
    message.success('Email copié !');
  };

  // Handle user actions
  const handleEdit = (user) => {
    setEditingUser(user);
    form.setFieldsValue(user);
    setIsModalOpen(true);
  };

  const handleDelete = (userId) => {
    message.success('Utilisateur supprimé');
  };

  const handleBulkAction = (action) => {
    message.success(`${selectedRowKeys.length} utilisateurs ${action}`);
    setSelectedRowKeys([]);
  };

  const avatarColorFor = (user: { id: number }) =>
    chartColors[user.id % chartColors.length];

  // Table columns
  const columns = [
    {
      title: 'Utilisateur',
      key: 'user',
      fixed: 'left',
      width: 280,
      render: (_, record) => (
        <Space>
          <Badge
            dot
            status={record.status === 'active' && record.lastActive.includes('min') ? 'success' : 'default'}
            offset={[-4, 36]}
          >
            <Avatar
              size={44}
              style={{
                backgroundColor: avatarColorFor(record),
                fontWeight: 600,
              }}
            >
              {record.firstName[0]}{record.lastName[0]}
            </Avatar>
          </Badge>
          <div>
            <Space size={4}>
              <Text strong>{record.firstName} {record.lastName}</Text>
              <Tag
                color={roleConfig[record.role].color}
                icon={roleConfig[record.role].icon}
                style={{ marginLeft: 4 }}
              >
                {roleConfig[record.role].label}
              </Tag>
            </Space>
            <br />
            <Text type="secondary" style={{ fontSize: 12 }}>
              ID: {record.id} • Actif {record.lastActive}
            </Text>
          </div>
        </Space>
      ),
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      width: 250,
      render: (email) => (
        <Space>
          <Text copyable={{ icon: <CopyOutlined />, tooltips: ['Copier', 'Copié !'] }}>
            {email}
          </Text>
        </Space>
      ),
    },
    {
      title: 'Statistiques',
      key: 'stats',
      width: 180,
      render: (_, record) => {
        if (record.role === 'client') {
          return (
            <Space size="large">
              <Statistic
                title={<Text type="secondary" style={{ fontSize: 11 }}>Demandes</Text>}
                value={record.requests}
                valueStyle={{ fontSize: 16, fontWeight: 600 }}
              />
              <Statistic
                title={<Text type="secondary" style={{ fontSize: 11 }}>Crédits</Text>}
                value={record.credits}
                valueStyle={{ fontSize: 16, fontWeight: 600, color: token.colorWarning }}
              />
            </Space>
          );
        } else if (record.role === 'tuner') {
          return (
            <Statistic
              title={<Text type="secondary" style={{ fontSize: 11 }}>Complétées</Text>}
              value={record.requests}
              valueStyle={{ fontSize: 16, fontWeight: 600 }}
            />
          );
        }
        return <Text type="secondary">—</Text>;
      },
    },
    {
      title: 'Statut',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      filters: [
        { text: 'Actif', value: 'active' },
        { text: 'Inactif', value: 'inactive' },
        { text: 'En attente', value: 'pending' },
      ],
      onFilter: (value, record) => record.status === value,
      render: (status) => (
        <Tag
          color={statusConfig[status].color}
          icon={statusConfig[status].icon}
        >
          {statusConfig[status].label}
        </Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Voir le profil">
            <Button
              type="text"
              icon={<EyeOutlined />}
              size="small"
            />
          </Tooltip>
          <Tooltip title="Modifier">
            <Button
              type="text"
              icon={<EditOutlined />}
              size="small"
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          <Popconfirm
            title="Supprimer cet utilisateur ?"
            description="Cette action est irréversible."
            onConfirm={() => handleDelete(record.id)}
            okText="Supprimer"
            cancelText="Annuler"
            okButtonProps={{ danger: true }}
          >
            <Tooltip title="Supprimer">
              <Button
                type="text"
                icon={<DeleteOutlined />}
                size="small"
                danger
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // Row selection config
  const rowSelection = {
    selectedRowKeys,
    onChange: (keys) => setSelectedRowKeys(keys),
    selections: [
      Table.SELECTION_ALL,
      Table.SELECTION_INVERT,
      Table.SELECTION_NONE,
    ],
  };

  // Tab items
  const tabItems = [
    {
      key: 'all',
      label: (
        <Space>
          <TeamOutlined />
          <span>Tous</span>
          <Badge count={roleCounts.all} showZero color={token.colorPrimary} />
        </Space>
      ),
    },
    {
      key: 'admin',
      label: (
        <Space>
          <CrownOutlined />
          <span>Administrateurs</span>
          <Badge count={roleCounts.admin} showZero color={token.colorError} />
        </Space>
      ),
    },
    {
      key: 'tuner',
      label: (
        <Space>
          <ToolOutlined />
          <span>Tuners</span>
          <Badge count={roleCounts.tuner} showZero color={token.colorInfo} />
        </Space>
      ),
    },
    {
      key: 'client',
      label: (
        <Space>
          <UserOutlined />
          <span>Clients</span>
          <Badge count={roleCounts.client} showZero color={token.colorSuccess} />
        </Space>
      ),
    },
  ];

  return (
      <Layout style={{
        minHeight: '100vh',
        background: `linear-gradient(145deg, ${token.colorBgLayout} 0%, ${token.colorBgContainer} 50%, ${token.colorBgLayout} 100%)`,
      }}>
        <Content style={{ padding: '28px 32px', maxWidth: 1400, margin: '0 auto', width: '100%' }}>
          {/* Header */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
            <div>
              <Title level={2} style={{ margin: 0, color: token.colorTextHeading }}>
                Gestion des utilisateurs
              </Title>
              <Text type="secondary">
                {users.length} utilisateurs • {users.filter(u => u.status === 'active').length} actifs
              </Text>
            </div>
            <Space>
              <Button icon={<ExportOutlined />}>
                Exporter
              </Button>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => {
                  setEditingUser(null);
                  form.resetFields();
                  setIsModalOpen(true);
                }}
              >
                Créer un utilisateur
              </Button>
            </Space>
          </div>

          {/* Role Tabs */}
          <Tabs
            activeKey={activeRole}
            onChange={setActiveRole}
            items={tabItems}
            style={{ marginBottom: 24 }}
            size="large"
          />

          {/* Stats Cards (shown when role is selected) */}
          {activeRole !== 'all' && (
            <Row gutter={16} style={{ marginBottom: 24 }}>
              <Col span={6}>
                <Card bordered={false}>
                  <Statistic
                    title={`Total ${roleConfig[activeRole]?.label || ''}`}
                    value={roleStats.total}
                    valueStyle={{ color: token.colorText }}
                  />
                </Card>
              </Col>
              <Col span={6}>
                <Card bordered={false}>
                  <Statistic
                    title="Actifs"
                    value={roleStats.active}
                    valueStyle={{ color: token.colorSuccess }}
                    prefix={<CheckCircleOutlined />}
                  />
                </Card>
              </Col>
              <Col span={6}>
                <Card bordered={false}>
                  <Statistic
                    title="Inactifs"
                    value={roleStats.inactive}
                    valueStyle={{ color: token.colorTextTertiary }}
                    prefix={<CloseCircleOutlined />}
                  />
                </Card>
              </Col>
              <Col span={6}>
                <Card bordered={false}>
                  <Statistic
                    title={activeRole === 'client' ? 'Crédits totaux' : 'Demandes traitées'}
                    value={activeRole === 'client' ? roleStats.totalCredits : roleStats.totalRequests}
                    valueStyle={{ color: token.colorWarning }}
                  />
                </Card>
              </Col>
            </Row>
          )}

          {/* Table Card */}
          <Card
            bordered={false}
            style={{ background: 'rgba(255,255,255,0.02)' }}
            styles={{ body: { padding: 0 } }}
          >
            {/* Toolbar */}
            <div style={{
              padding: '16px 24px',
              borderBottom: '1px solid rgba(255,255,255,0.06)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 16,
            }}>
              <Space size="middle" wrap>
                <Input
                  placeholder="Rechercher par nom ou email..."
                  prefix={<SearchOutlined style={{ color: 'rgba(255,255,255,0.3)' }} />}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  style={{ width: 280 }}
                  allowClear
                />
                <Select
                  value={filterStatus}
                  onChange={setFilterStatus}
                  style={{ width: 160 }}
                  options={[
                    { value: 'all', label: 'Tous les statuts' },
                    { value: 'active', label: 'Actifs' },
                    { value: 'inactive', label: 'Inactifs' },
                    { value: 'pending', label: 'En attente' },
                  ]}
                />
                <Button icon={<ReloadOutlined />}>Actualiser</Button>
              </Space>

              {/* Bulk actions */}
              {selectedRowKeys.length > 0 && (
                <Space
                  style={{
                    padding: '8px 16px',
                    background: hexToRgba(token.colorPrimary, 0.1),
                    borderRadius: 8,
                    border: `1px solid ${hexToRgba(token.colorPrimary, 0.2)}`,
                  }}
                >
                  <Text style={{ color: token.colorPrimary }}>
                    {selectedRowKeys.length} sélectionné{selectedRowKeys.length > 1 ? 's' : ''}
                  </Text>
                  <Button size="small" onClick={() => handleBulkAction('activés')}>
                    Activer
                  </Button>
                  <Button size="small" onClick={() => handleBulkAction('désactivés')}>
                    Désactiver
                  </Button>
                  <Button size="small" danger onClick={() => handleBulkAction('supprimés')}>
                    Supprimer
                  </Button>
                </Space>
              )}

              <Text type="secondary">
                {filteredUsers.length} résultat{filteredUsers.length > 1 ? 's' : ''}
              </Text>
            </div>

            {/* Table */}
            <Table
              rowSelection={rowSelection}
              columns={columns}
              dataSource={filteredUsers}
              rowKey="id"
              pagination={{
                pageSize: 10,
                showSizeChanger: true,
                showTotal: (total, range) => `${range[0]}-${range[1]} sur ${total}`,
                pageSizeOptions: ['10', '20', '50'],
              }}
              scroll={{ x: 1000 }}
              locale={{
                emptyText: (
                  <div style={{ padding: '40px 0' }}>
                    <UserOutlined style={{ fontSize: 48, color: 'rgba(255,255,255,0.2)', marginBottom: 16, display: 'block' }} />
                    <Text type="secondary">Aucun utilisateur trouvé</Text>
                  </div>
                ),
              }}
            />
          </Card>

          {/* Create/Edit Modal */}
          <Modal
            title={editingUser ? 'Modifier l\'utilisateur' : 'Créer un utilisateur'}
            open={isModalOpen}
            onCancel={() => setIsModalOpen(false)}
            footer={[
              <Button key="cancel" onClick={() => setIsModalOpen(false)}>
                Annuler
              </Button>,
              <Button key="submit" type="primary" onClick={() => {
                form.validateFields().then(values => {
                  message.success(editingUser ? 'Utilisateur modifié' : 'Utilisateur créé');
                  setIsModalOpen(false);
                });
              }}>
                {editingUser ? 'Enregistrer' : 'Créer'}
              </Button>,
            ]}
            width={600}
          >
            <Form
              form={form}
              layout="vertical"
              style={{ marginTop: 24 }}
            >
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    label="Prénom"
                    name="firstName"
                    rules={[{ required: true, message: 'Requis' }]}
                  >
                    <Input placeholder="Prénom" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="Nom"
                    name="lastName"
                    rules={[{ required: true, message: 'Requis' }]}
                  >
                    <Input placeholder="Nom" />
                  </Form.Item>
                </Col>
              </Row>
              <Form.Item
                label="Email"
                name="email"
                rules={[
                  { required: true, message: 'Requis' },
                  { type: 'email', message: 'Email invalide' },
                ]}
              >
                <Input prefix={<MailOutlined />} placeholder="email@exemple.com" />
              </Form.Item>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    label="Rôle"
                    name="role"
                    rules={[{ required: true, message: 'Requis' }]}
                  >
                    <Select
                      placeholder="Sélectionner un rôle"
                      options={[
                        { value: 'admin', label: '👑 Administrateur' },
                        { value: 'tuner', label: '🔧 Tuner' },
                        { value: 'client', label: '👤 Client' },
                      ]}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="Statut"
                    name="status"
                    initialValue="active"
                  >
                    <Select
                      options={[
                        { value: 'active', label: '✅ Actif' },
                        { value: 'inactive', label: '⏸️ Inactif' },
                        { value: 'pending', label: '⏳ En attente' },
                      ]}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Form>
          </Modal>
        </Content>
      </Layout>
  );
};

export default UserManagementAntD;